import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import useAntibodyMapState from 'antibodies/antibody-map/useAntibodyMapState';
import useTransientCurrentPolymerModel from 'molbio/hooks/useTransientCurrentPolymerModel';
import ProteinMapContainer from 'molbio/maps/standalone/ProteinMapContainer';
import Protein from 'molbio/proteins/Protein';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface DomainPreviewProps {
  antibodyId: string;
  selectedProteinId: string;
  toggleExpandPreview: () => void;
}

export default function DomainPreview({
  antibodyId,
  selectedProteinId,
  toggleExpandPreview,
}: DomainPreviewProps): JSX.Element {
  const protein = useTransientCurrentPolymerModel(selectedProteinId) as Protein | null;
  const {
    state: {expandPreview, showMinimap},
    setShowMinimap,
  } = useAntibodyMapState(antibodyId);

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.header)}>
        <BenchChip id={selectedProteinId} size={ChipSize.SMALL} />
        <div className={css(styles.buttonsWrapper)}>
          {expandPreview && (
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              className={showMinimap ? css(styles.activeMinimap) : ''}
              icon="antibody"
              iconAltText={showMinimap ? 'Hide minimap' : 'Show minimap'}
              onClick={() => {
                setShowMinimap(!showMinimap);
              }}
              size={ButtonSize.SMALL}
            />
          )}
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            icon={expandPreview ? 'collapse-right-full' : 'expand-right-full'}
            iconAltText={expandPreview ? 'Collapse domain preview' : 'Expand domain preview'}
            onClick={toggleExpandPreview}
            size={ButtonSize.SMALL}
          />
        </div>
      </div>
      <div className={css(expandPreview ? styles.proteinMapWrapperExpanded : styles.proteinMapWrapper)}>
        {protein == null && <CenteredHorizontalThrobber />}
        {protein != null && <ProteinMapContainer protein={protein} />}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    backgroundColor: Color.GRAY_100,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    borderRadiusTopLeft: '4px',
    borderRadiusTopRight: '4px',
    flex: '0 0 44px',
  },
  buttonsWrapper: {
    display: 'flex',
    gap: '8px',
  },
  activeMinimap: {
    color: Color.BLUE_400,
  },
  icon: {
    cursor: 'pointer',
    width: '16px',
    height: '16px',
  },
  proteinMapWrapper: {
    flex: '0 0 236px',
  },
  proteinMapWrapperExpanded: {
    flex: '1 0 auto',
  },
});
